import { getFeatureToggles } from "@/helpers/featureToggles";

export const businessLogic = {
  cancellationCutOffMinutes: 20,
  returnCutOffDays: 14,
  loyaltyTiers: {
    1: {
      name: "White",
      lowerBound: 0,
      upperBound: 189.99,
    },
    2: {
      name: "Grey",
      lowerBound: 190,
      upperBound: 379.99,
    },
    3: {
      name: "Black",
      lowerBound: 380,
      upperBound: 570,
    },
  },
  lowStockQuantity: 10,
  signupDelaySeconds: 30,
  maxCartTotalQuantity: 8,
  maxCartProductQuantity: null,
  preventSplitCart: true,
  loyaltyPointsConversion: 20,
};

export const regionFeatureToggles = {
  gbp: {
    smsStockNotificationsEnabled: true,
    phoneNumberSignUp: true,
    eddEnabled: false,
    dataCollectionEnabled: true,
  },
  aud: {
    smsStockNotificationsEnabled: true,
    phoneNumberSignUp: true,
    eddEnabled: false,
    dataCollectionEnabled: true,
  },
  usd: {
    smsStockNotificationsEnabled: true,
    phoneNumberSignUp: true,
    eddEnabled: false,
    dataCollectionEnabled: true,
  },
  eur: {
    smsStockNotificationsEnabled: true,
    phoneNumberSignUp: true,
    eddEnabled: false,
    dataCollectionEnabled: true,
  },
};

export const klaviyoLists = {
  gbp: {
    accountId: "KKdVHx",
    listId: "PBQpGD",
  },
  aud: {
    accountId: "QLUUqb",
    listId: "LuKxsY",
  },
  usd: {
    accountId: "LvLjjr",
    listId: "N6CnRv",
  },
  eur: {
    accountId: "Q8ECcx",
    listId: "Jxz7Q4",
  },
};

export const localeUrls = {
  gbp: "ratandboa.com",
  aud: "aus.ratandboa.com",
  usd: "us.ratandboa.com",
  eur: "eur.ratandboa.com",
};

// Nosto account IDs per currency, mirroring the script tags in
// shopify/layout/theme.liquid. Used by the Nosto Search API integration.
export const nostoAccountIds = {
  gbp: "shopify-9429062",
  aud: "shopify-11919914",
  usd: "shopify-11919918",
  eur: "shopify-1828061261",
};

// Account ID used on the redev/dev environment, regardless of currency.
export const nostoDevAccountId = "shopify-60981248218";
export const nostoDevHost = "redev.ratandboa.com";

// Nosto search facet value allowlists, keyed by facet field.
//
// Nosto "terms" facets (e.g. Category, field `categories`) return EVERY value
// found in the result set - for Category that means every Shopify collection a
// product belongs to, including internal/marketing collections (e.g.
// "Event Dressing", "Flexify", "PBRF1", "LTK Gifting"). Nosto has no dashboard
// option to whitelist terms-facet values, so we curate the customer-facing
// categories here.
//
// Rules:
// - Values must match the Nosto facet value (the Shopify collection title)
//   EXACTLY (case-sensitive). Values not present in a given result set are
//   simply skipped, so it's safe to list every category here.
// - Display order in the filter UI follows this array's order.
// - A field with no entry here (e.g. `ai.primaryColor` / Colour) shows all
//   returned values as normal.
export const nostoFacetValueAllowlists = {
  categories: [
    "Dresses",
    "Maxi Dresses",
    "Midi Dresses",
    "Mini Dresses",
    "Party Dresses",
    "Holiday Dresses",
    "Beach Dresses",
    "Bra-friendly Dresses",
    "Beachwear",
    "Swimwear",
    "Cover Ups",
    "Matching Sets",
    "Skirts",
    "Tailoring",
    "Tops",
    "Trousers",
    "Knitwear",
    "Undergarments",
    "Jewellery",
    "Necklaces",
    "Earrings",
    "Rings",
    "Bracelets",
    "Bags",
    "Capes",
    "Accessories",
  ],
};

// Sort fields sent to Nosto when Category Merchandising 2.0 drives a
// collection listing (ft-nostoCategoryEnabled). Keys are the collection
// store's sort values. MANUAL (the default) is deliberately absent: with no
// sort Nosto returns the merchandised order, and any explicit sort overrides
// merchandising rules. PRICE direction follows the dropdown (reverse flag).
export const nostoCollectionSortFields = {
  BEST_SELLING: { field: "stats.buys", order: "desc" },
  CREATED: { field: "datePublished", order: "desc" },
  PRICE: { field: "price", order: "asc" },
};

// Helper to resolve the Nosto account ID for the current currency/host.
// Priority: ?ft-nostoAccountId override -> dev host -> per-currency map.
export function getNostoAccountId(currencyCode) {
  const { nostoAccountId } = getFeatureToggles();
  if (nostoAccountId) {
    return nostoAccountId;
  }

  if (
    typeof window !== "undefined" &&
    window.location?.hostname === nostoDevHost
  ) {
    return nostoDevAccountId;
  }

  if (!currencyCode) return null;

  return nostoAccountIds[currencyCode.toLowerCase()] || null;
}

const libreCollectionBannerMessage = {
  text: "New In: The Attimi Capsule",
  link: "/collections/the-attimi-capsule",
};

const gbpBannerMessages = [
  "Free Shipping on Orders Over £200",
  libreCollectionBannerMessage,
];

const audBannerMessages = [
  "Free Shipping on Australian Orders over $250",
  libreCollectionBannerMessage,
];

const usdBannerMessages = [
  "Free Shipping on Orders Over $200",
  libreCollectionBannerMessage,
];

const eurBannerMessages = [
  "Free Shipping on Orders Over €200",
  libreCollectionBannerMessage,
];

export const bannerContent = {
  gbp: {
    rotate: true,
    signedOutMessages: gbpBannerMessages,
    signedInMessages: gbpBannerMessages,
    messages: gbpBannerMessages,
    rotationIntervalMs: 9000,
    color: "black",
    countdown: {
      enabled: false,
      label: "SALE ENDS IN",
      suffix: "",
      link: "/collections/all",
      end: "2026-10-01T17:00:00Z",
      timeZone: "Europe/London",
      color: "red",
    },
  },
  aud: {
    rotate: true,
    signedOutMessages: audBannerMessages,
    signedInMessages: audBannerMessages,
    messages: audBannerMessages,
    rotationIntervalMs: 9000,
    color: "black",
    countdown: {
      enabled: false,
      label: "SALE ENDS IN",
      suffix: "",
      link: "/collections/all",
      end: "2026-10-01T03:00:00Z",
      timeZone: "Australia/Melbourne",
      color: "red",
    },
  },
  usd: {
    rotate: true,
    signedOutMessages: usdBannerMessages,
    signedInMessages: usdBannerMessages,
    messages: usdBannerMessages,
    rotationIntervalMs: 7000,
    color: "black",
    countdown: {
      enabled: false,
      label: "SALE ENDS IN",
      suffix: "",
      link: "/collections/all",
      end: "2026-10-01T17:00:00Z",
      timeZone: "America/New_York",
      color: "red",
    },
  },
  eur: {
    rotate: true,
    signedOutMessages: eurBannerMessages,
    signedInMessages: eurBannerMessages,
    messages: eurBannerMessages,
    rotationIntervalMs: 9000,
    color: "black",
    countdown: {
      enabled: false,
      label: "SALE ENDS IN",
      suffix: "",
      link: "/collections/all",
      end: "2026-10-01T17:00:00Z",
      timeZone: "Europe/Amsterdam",
      color: "red",
    },
  },
};

export const signUpBonus = {
  gbp: 20,
  aud: 40,
  usd: 30,
  eur: "10%",
};

export const birthdayBonus = {
  gbp: 10,
  aud: 20,
  usd: 15,
  eur: 10,
};

export const shippingRules = {
  gbp: {
    freeAboveAmount: {
      amount: 200,
      currencyCode: "gbp",
    },
    // Level 3 and 4 get free standard shipping on every order.
    freeShippingFromLoyaltyLevel: 3,
  },
  usd: {
    freeAboveAmount: {
      amount: 200,
      currencyCode: "usd",
    },
  },
  aud: {
    freeForCountry: "AU",
    freeAboveAmount: {
      amount: 250,
      currencyCode: "aud",
    },
    freeShippingFromLoyaltyLevel: 3,
  },
  eur: {
    freeForCountry: null,
    freeAboveAmount: {
      amount: 200,
      currencyCode: "eur",
    },
  },
};

export const paymentMethods = {
  gbp: {
    klarnaEnabled: true,
    clearpayEnabled: true,
    pdpMessage: {
      provider: "Klarna",
      instalments: 3,
    },
  },
  usd: {
    klarnaEnabled: false,
    clearpayEnabled: true,
    pdpMessage: {
      provider: "Clearpay",
      instalments: 4,
    },
  },
  aud: {
    klarnaEnabled: false,
    clearpayEnabled: true,
    pdpMessage: {
      provider: "Afterpay",
      instalments: 3,
    },
  },
  eur: {
    klarnaEnabled: true,
    clearpayEnabled: false,
    pdpMessage: {
      provider: "Klarna",
      instalments: 3,
    },
  },
};

export const orderCutOffForNextDay = {
  gbp: {
    time: "14:00:00",
    timezone: "Europe/London",
  },
  aud: {
    time: "14:00:00",
    timezone: "Australia/Melbourne",
  },
  usd: {
    time: "14:00:00",
    timezone: "Europe/London",
  },
  eur: {
    time: "14:00:00",
    timezone: "Europe/Amsterdam",
  },
};

export const collectionGateAccessTimes = {
  // Attimi launch. Levels 4–2 share one open on Thu 22 Oct.
  // Level 1 and Everyone use the 25 Oct GMT start times.
  gbp: {
    // 22 Oct is still BST (clocks go back 25 Oct). 09:00 UK = 08:00 UTC.
    level4: "2026-10-22T08:00:00Z", // 09:00 BST
    level3: "2026-10-22T08:00:00Z",
    level2: "2026-10-22T08:00:00Z",
    level1: "2026-10-25T09:00:00Z", // 09:00 GMT
    disabled: "2026-10-25T09:30:00Z", // 09:30 GMT — Everyone
  },
  eur: {
    level4: "2026-10-22T08:00:00Z", // 10:00 CEST
    level3: "2026-10-22T08:00:00Z",
    level2: "2026-10-22T08:00:00Z",
    level1: "2026-10-25T09:00:00Z", // 09:00 GMT / 10:00 CET
    disabled: "2026-10-25T09:30:00Z", // 09:30 GMT / 10:30 CET — Everyone
  },
  usd: {
    // 19:00 EDT and 16:00 PDT are the same instant
    level4: "2026-10-22T23:00:00Z", // 19:00 EDT / 16:00 PDT
    level3: "2026-10-22T23:00:00Z",
    level2: "2026-10-22T23:00:00Z",
    level1: "2026-10-25T16:00:00Z", // 16:00 GMT / 12:00 EDT / 09:00 PDT
    disabled: "2026-10-25T16:30:00Z", // 16:30 GMT / 12:30 EDT / 09:30 PDT — Everyone
  },
  aud: {
    // No separate Thursday time was given, so levels 4–2 open with the 09:00 GMT launch.
    // 25 Oct GMT column is 23:00 then 00:00. Melbourne is on AEDT (UTC+11) by then.
    level4: "2026-10-22T09:00:00Z", // 20:00 AEDT
    level3: "2026-10-22T09:00:00Z",
    level2: "2026-10-22T09:00:00Z",
    level1: "2026-10-24T23:00:00Z", // 23:00 GMT 24 Oct / 10:00 AEDT 25 Oct
    disabled: "2026-10-25T00:00:00Z", // 00:00 GMT / 11:00 AEDT — Everyone
  },
};

export const saleAccessTimes = {
  // 27 Sep 2026 sale launch — local sheet times converted to UTC
  gbp: {
    level4: "2026-09-27T13:00:00Z", // 14:00 BST
    level3: "2026-09-27T14:00:00Z", // 15:00 BST
    level2: "2026-09-27T15:00:00Z", // 16:00 BST
    level1: "2026-09-27T16:00:00Z", // 17:00 BST
    disabled: "2026-09-27T17:00:00Z", // 18:00 BST — Everyone
  },
  eur: {
    level4: "2026-09-27T13:00:00Z", // 15:00 CEST
    level3: "2026-09-27T14:00:00Z", // 16:00 CEST
    level2: "2026-09-27T15:00:00Z", // 17:00 CEST
    level1: "2026-09-27T16:00:00Z", // 18:00 CEST
    disabled: "2026-09-27T17:00:00Z", // 19:00 CEST — Everyone
  },
  usd: {
    // 06:00 PDT and 09:00 EDT are the same instant
    level4: "2026-09-27T13:00:00Z", // 06:00 PDT / 09:00 EDT
    level3: "2026-09-27T14:00:00Z", // 07:00 PDT / 10:00 EDT
    level2: "2026-09-27T15:00:00Z", // 08:00 PDT / 11:00 EDT
    level1: "2026-09-27T16:00:00Z", // 09:00 PDT / 12:00 EDT
    disabled: "2026-09-27T17:00:00Z", // 10:00 PDT / 13:00 EDT — Everyone
  },
  aud: {
    // Melbourne still AEST (UTC+10) on 27 Sep — AEDT starts 4 Oct.
    // Every slot is on 27 Sep locally; Level 4 is 26 Sep once in UTC.
    level4: "2026-09-26T23:00:00Z", // 09:00 AEST on 27 Sep
    level3: "2026-09-27T00:00:00Z", // 10:00 AEST
    level2: "2026-09-27T01:00:00Z", // 11:00 AEST
    level1: "2026-09-27T02:00:00Z", // 12:00 AEST
    disabled: "2026-09-27T03:00:00Z", // 13:00 AEST — Everyone
  },
};

/** Highest loyalty tier from customer tags (4 → 1), or 0 if none. */
export function getCollectionGateTierLevel(userTags = []) {
  if (userTags.includes("tier: Level 4")) return 4;
  if (userTags.includes("tier: Level 3")) return 3;
  if (userTags.includes("tier: Level 2")) return 2;
  if (userTags.includes("tier: Level 1")) return 1;
  return 0;
}

/** ISO access time for a tier level within a currency’s gate schedule. */
export function getCollectionGateAccessTime(currencyAccessTimes, tierLevel) {
  if (!currencyAccessTimes) return null;
  if (tierLevel === 4) return currencyAccessTimes.level4;
  if (tierLevel === 3) return currencyAccessTimes.level3;
  if (tierLevel === 2) return currencyAccessTimes.level2;
  if (tierLevel === 1) return currencyAccessTimes.level1;
  return currencyAccessTimes.disabled;
}

export const salePreviewTimes = {
  gbp: {
    datetime: "2026-03-01T12:00:00Z", // 10am BST
    displayText: "Sale starts 12pm 01.03.26 BST",
  },
  usd: {
    datetime: "2026-03-01T13:00:00Z", // 11am EDT
    displayText: "Sale starts 1pm 01.03.26 EDT",
  },
  eur: {
    datetime: "2026-03-01T13:00:00Z", // 11am CEST
    displayText: "Sale starts 1pm 01.03.26 CEST",
  },
  aud: {
    datetime: "2026-03-01T13:00:00Z", // 10am AEST (previous day UTC)
    displayText: "Sale starts 1pm 01.03.26 AEST",
  },
};

// Sign-up bonus for 10% variant (GBP only)
export const signUpBonusTenPercentVariant = {
  gbp: "10%",
  aud: 40,
  usd: 30,
  eur: "10%",
};

// Sign-up bonus for 30 pound variant (GBP only)
export const signUpBonusThirtyPoundVariant = {
  gbp: 30,
  aud: 40,
  usd: 30,
  eur: "10%",
};

// Loyalty list IDs for sign-up flows
export const loyaltyListIds = {
  gbp: "WfsAVx",
  // Other regions will use backend defaults
};

// Helper function to get the appropriate sign-up bonus
export function getSignUpBonus(currencyCode, featureToggles = {}) {
  if (!currencyCode) return null;

  const currency = currencyCode.toLowerCase();
  const { thirtyPoundVariant = false } = featureToggles;

  // For GBP, use the 30 pound variant if the toggle is on (replaces 10% variant for A/B test: 20 vs 30)
  if (currency === "gbp" && thirtyPoundVariant) {
    return signUpBonusThirtyPoundVariant[currency];
  }

  // Otherwise use the standard sign-up bonus (20 pounds for GBP)
  return signUpBonus[currency];
}

// Sale-specific PDP content for Shipping & Returns accordion
// Active: 1st December 5pm (7am AEST for Aus) - 13th December
export const salePdpContent = {
  gbp: {
    exchanges: {
      title: "Exchanges",
      content: [
        "Exchange costs are covered by Rat & Boa",
        "The cost of returning an already exchanged piece is covered by the customer",
        "Sale items are eligible for straight exchanges (same item, different size)",
      ],
    },
    storeCredit: {
      title: "Store Credit",
      content: [
        "Get 10% store credit on all returns (including sale items) if you choose store credit instead of a refund",
      ],
    },
  },
  usd: {
    exchanges: null,
    storeCredit: {
      title: "Store Credit",
      content: [
        "Get 10% store credit on all returns (including sale items) if you choose store credit instead of a refund",
      ],
    },
  },
  eur: {
    exchanges: null,
    storeCredit: {
      title: "Store Credit",
      content: [
        "Get 10% store credit on all returns (including sale items) if you choose store credit instead of a refund",
      ],
    },
  },
  aud: {
    exchanges: {
      title: "Exchanges",
      content: [
        "Exchange costs are covered by Rat & Boa",
        "The cost of returning an already exchanged piece is covered by the customer",
        "Sale items are eligible for straight exchanges (same item, different size)",
      ],
    },
    storeCredit: {
      title: "Store Credit",
      content: [
        "Get 10% store credit on all returns (including sale items) if you choose store credit instead of a refund",
      ],
    },
  },
};

// Region-specific shipping & returns list items (PDP accordion)
export const shippingReturnsListItems = {
  gbp: [
    "Return items within 14 days of receipt",
    "Use our returns label or your own courier",
    "Return costs covered by the customer",
    "Level 4 members are exempt from return costs",
  ],
  usd: [
    "Return items within 14 days of receipt",
    "Use our returns label or your own courier",
    "Return costs covered by the customer",
    "Level 4 members are exempt from return costs",
  ],
  eur: [
    "Return items within 14 days of receipt",
    "Use our returns label or your own courier",
    "Return costs covered by the customer",
    "Level 4 members are exempt from return costs",
  ],
  aud: [
    "Return items within 14 days of receipt",
    "Use our returns label or your own courier",
    "Return costs covered by the customer",
    "Level 4 members are exempt from return costs",
  ],
};
